import { useState, type FunctionComponent } from "react";
import { Alert, Button, Card, message, Tabs, Typography } from "antd";
import { useTranslation } from "react-i18next";

import { productActions, RequestStatus } from "@fitness-app/app-store";
import {
  EndAccessStrategy,
  PaymentTypeOption,
  ProductChargeType,
  ProductPriceAction,
  ProductStatus,
  ProductType,
  type OneTimePrice,
  type RecurringInterval,
  type RecurringPrice,
  type UpdateProductPriceDTO,
} from "@fitness-app/data-models/entities/Product";

import ModalForm from "~/components/ModalForm/ModalForm";
import { BillingPeriod } from "~/modules/Products/constants/productForm";
import PricesForm from "~/modules/Products/Product/PricesForm/PricesForm";
import { CreateCheckoutVariantLink } from "~/modules/Products/Product/ProductDetails/components/CreateCheckoutVariantLink";
import { ProductCheckoutVariants } from "~/modules/Products/Product/ProductDetails/components/ProductCheckoutVariants";
import { ProductSalesFunnels } from "~/modules/Products/Product/ProductDetails/components/ProductSalesFunnels";
import ProductPricesBrowser from "~/modules/Products/Product/ProductPrices/ProductPricesBrowser";
import { type PricesFormModel, type ProductFormModel } from "~/modules/Products/ProductForm/types";
import { useAppDispatch, useAppSelector } from "~/store/initializeStore";

/**
 * Everything that sells the product: its prices, the checkout variants built from them and
 * the funnels that lead to them. Split out of the product details tab, which mounted all of
 * it eagerly and stalled on products carrying a few hundred prices.
 */
const ProductSales: FunctionComponent = () => {
  const { t } = useTranslation(["products", "common"]);
  const { details, productDetailsStatus, productPricesActions } = useAppSelector((store) => store.product);
  const [showPricesForm, togglePricesForm] = useState(false);
  const dispatch = useAppDispatch();

  const handlePricesSubmit = async (model: PricesFormModel) => {
    if (!details?.id) {
      void message.warning("Problem with load form model");
      return;
    }

    const formattedData: UpdateProductPriceDTO[] = model.prices.map((price) => {
      const formattedPrice =
        price.paymentType === PaymentTypeOption.Recurring && price.billingPeriod
          ? ({
              amount: price.amount,
              currency: price.currency,
              type: PaymentTypeOption.Recurring,
              active: true,
              interval_count:
                price.billingPeriod === BillingPeriod.Custom
                  ? price.billingIntervalCount || 1
                  : Number(price.billingPeriod.split("-")[0]),
              interval:
                price.billingPeriod === BillingPeriod.Custom
                  ? price.billingInterval || "month"
                  : (price.billingPeriod.split("-")[1] as RecurringInterval),

              paymentClient: price.paymentClient,
              ...(price.installments && { installments: price.installments }),
              ...(price.activeCustomDuration && {
                customDuration: price.customDuration
                  ? {
                      count: price.customDuration.count,
                      unit: price.customDuration.unit,
                    }
                  : null,
              }),
            } as RecurringPrice)
          : ({
              amount: price.amount,
              currency: price.currency,
              active: true,
              type: PaymentTypeOption.OneTime,
              paymentClient: price.paymentClient,
              endAccessStrategy: price.endAccessStrategy || EndAccessStrategy.Cancel,
              ...(price.billingPeriod && price.billingPeriod !== BillingPeriod.ToCancel
                ? {
                    interval_count:
                      price.billingPeriod === BillingPeriod.Custom
                        ? price.billingIntervalCount || 1
                        : Number(price.billingPeriod.split("-")[0]),
                    interval:
                      price.billingPeriod === BillingPeriod.Custom
                        ? price.billingInterval || "month"
                        : (price.billingPeriod.split("-")[1] as RecurringInterval),
                  }
                : {}),
            } as OneTimePrice);

      return {
        price: formattedPrice,
        productId: details.id,
        action: ProductPriceAction.Create,
      };
    });

    await dispatch(
      productActions.updateProductPrices({
        onFailure: () => {
          void message.error(t("errorWhenUpdatingPrice"));
        },
        onSuccess: () => togglePricesForm(false),
        payload: {
          prices: formattedData,
        },
      }),
    );
  };

  if (details?.chargeType === ProductChargeType.FREE) {
    return null;
  }

  return (
    <>
      <Card loading={productDetailsStatus === RequestStatus.SUBSCRIBING && !details}>
        {details?.status !== ProductStatus.ACTIVE && details?.prices.length ? (
          <Alert showIcon message={t("productDetails.inactivePrices")} type="warning" />
        ) : null}

        {details?.prices.length && details.status === ProductStatus.ACTIVE ? (
          <Tabs defaultActiveKey="prices">
            <Tabs.TabPane key="prices" tab="Ceny">
              <div className="flex justify-end gap-3 py-4">
                <Button type="primary" onClick={() => togglePricesForm(true)}>
                  {t("button.addNewPrices")}
                </Button>
                <CreateCheckoutVariantLink productSlug={details.slug} productId={details.id} prices={details.prices} />
              </div>
              <ProductPricesBrowser
                productSlug={details.slug ?? ""}
                prices={details.prices}
                productId={details.id}
                productName={details.name}
              />
              <div className="mt-8">
                <Typography.Text type="secondary">{t("prices.requirements")}</Typography.Text>
              </div>
            </Tabs.TabPane>
            <Tabs.TabPane key="variants" tab="Warianty koszyka" destroyInactiveTabPane>
              <ProductCheckoutVariants prices={details.prices} productId={details.id} />
            </Tabs.TabPane>
            <Tabs.TabPane key="salesFunnels" tab="Funnels" destroyInactiveTabPane>
              <ProductSalesFunnels productId={details.id} />
            </Tabs.TabPane>
          </Tabs>
        ) : null}
      </Card>

      <ModalForm<ProductFormModel>
        open={showPricesForm}
        width={900}
        onCancel={() => togglePricesForm(false)}
        title={t<string>("products:formPrices.title")}
        loading={!!productPricesActions?.length}
      >
        <PricesForm
          onSubmit={handlePricesSubmit}
          model={null}
          productType={details?.type || ProductType.PERSONAL_CLIENT_SERVICE}
        />
      </ModalForm>
    </>
  );
};

export default ProductSales;
